import {
  Control,
  useFieldArray,
  useWatch,
  useFormContext,
} from 'react-hook-form';
import CircuitEditorialForm from './CircuitEditorialForm';
import { startTransition, useEffect, useState, memo, useRef } from 'react';
import clsx from 'clsx';
import { createEmptyCircuitDays } from '@/utils/circuits/createEmptyCircuitDays';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import DuplicateIcon from '@/assets/icons/copy-03.svg?react';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useCircuitDayManagement } from '@/hooks/useCircuitDayManagement';
import CircuitDayOperationModal from './CircuitDayOperationModal';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { useTranslation } from '@/i18n';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';
import { ParagraphOverrides } from '@/api/circuits.api';

export type EditorialMode = 'day' | 'list';

interface CircuitDaysListProps {
  control: Control<CircuitFormDataWithTranslations>;
  language: SupportedLocale;
  circuitDayErrorIndex?: number | null;
  circuitCode?: number;
  isViewMode?: boolean;
  paragraphOverrides?: ParagraphOverrides;
  suppressedOverrideFields?: Set<string>;
}

const DayItem = memo(
  ({
    index,
    style,
    isSelected,
    onClick,
    onDuplicate,
    onRemove,
    showRemove,
    onAddDay,
    circuitCode,
    isViewMode = false,
  }: {
    index: number;
    style: React.CSSProperties;
    isSelected: boolean;
    onClick: () => void;
    onDuplicate: () => void;
    onRemove: () => void;
    showRemove: boolean;
    onAddDay: () => void;
    circuitCode?: number;
    isViewMode?: boolean;
  }) => {
    const { t } = useTranslation();
    return (
      <div
        style={style}
        className={clsx('flex justify-between pr-4 border rounded-md', {
          'bg-brand-50 border-brand-600': isSelected,
          'bg-white': !isSelected,
        })}
      >
        <div
          className="w-full h-full pl-4 py-4 flex items-center hover:cursor-pointer"
          onClick={onClick}
        >
          <h2 className="text-base font-medium text-black w-full whitespace-normal wrap-break-word">
            {t('circuits.daysList.day', { index: index + 1 })}
          </h2>
        </div>
        {circuitCode !== undefined && (
          <div
            className="w-full h-full py-3 hover:cursor-pointer"
            onClick={onClick}
          >
            <h2 className="text-base font-medium text-black w-full h-full">
              C{circuitCode.toString().padStart(4, '0')}J
              {(index + 1).toString().padStart(2, '0')}
            </h2>
          </div>
        )}
        {!isViewMode && (
          <div className="flex gap-2 w-fit">
            <StyledTooltipContainer
              tooltipMessage={t('circuits.daysList.addDay')}
            >
              <PlusIcon
                className="size-5 fill-gray-500 hover:cursor-pointer"
                onClick={onAddDay}
              />
            </StyledTooltipContainer>
            <StyledTooltipContainer
              tooltipMessage={t('circuits.daysList.duplicate')}
            >
              <DuplicateIcon
                className="size-5 stroke-gray-500 hover:cursor-pointer"
                onClick={onDuplicate}
              />
            </StyledTooltipContainer>
            {showRemove && (
              <StyledTooltipContainer
                tooltipMessage={t('circuits.daysList.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-gray-500 hover:cursor-pointer"
                  onClick={onRemove}
                />
              </StyledTooltipContainer>
            )}
          </div>
        )}
      </div>
    );
  },
);

const CircuitDaysList = ({
  control,
  language,
  circuitDayErrorIndex = null,
  circuitCode,
  isViewMode = false,
  paragraphOverrides,
  suppressedOverrideFields,
}: CircuitDaysListProps) => {
  const { t } = useTranslation();
  const [editorialMode, setEditorialMode] = useState<EditorialMode>('day');

  const { setValue } = useFormContext<CircuitFormDataWithTranslations>();
  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: 'circuitDays',
  });
  const { remove: removeTranslation, insert: insertTranslation } =
    useTranslationFieldArray({
      control,
      name: 'circuitDays',
    });

  const days = useWatch({
    control,
    name: `circuitDays`,
  });

  const [userSelectedDayIndex, setUserSelectedDayIndex] = useState(0);
  const isInitialized = useRef(false);

  const hasNavigatedToErrorRef = useRef(false);
  const prevErrorIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const currentErrorIndex = circuitDayErrorIndex;
    const isNewError =
      currentErrorIndex !== null &&
      currentErrorIndex !== prevErrorIndexRef.current &&
      !hasNavigatedToErrorRef.current;

    if (isNewError) {
      startTransition(() => {
        setUserSelectedDayIndex(currentErrorIndex);
      });
      hasNavigatedToErrorRef.current = true;
    }

    if (currentErrorIndex !== prevErrorIndexRef.current) {
      if (currentErrorIndex === null) {
        hasNavigatedToErrorRef.current = false;
      }
      prevErrorIndexRef.current = currentErrorIndex;
    }
  }, [circuitDayErrorIndex]);

  const handleDayClick = (index: number) => {
    setUserSelectedDayIndex(index);
    hasNavigatedToErrorRef.current = false;
    if (editorialMode === 'list') {
      const element = document.getElementById(`circuit-day-${index}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const nbDays = useWatch({ control, name: 'nbDays' });

  const translations = useWatch({ control, name: 'translations' });

  const {
    handleAddDay,
    handleRemoveDay,
    handleDuplicateDay,
    modalState,
    closeModal,
  } = useCircuitDayManagement({
    fields,
    insert,
    remove,
    setValue,
    displayedDayIndex: userSelectedDayIndex,
    setDisplayedDayIndex: setUserSelectedDayIndex,
    insertTranslation,
    removeTranslation,
    translations,
  });

  // Initialize days on first render
  useEffect(() => {
    if (!isInitialized.current && nbDays !== undefined) {
      if (fields.length < nbDays) {
        append(createEmptyCircuitDays(nbDays - fields.length));
      } else if (fields.length > nbDays) {
        const indexesToRemove = Array.from(
          { length: fields.length - nbDays },
          (_, i) => i + +nbDays,
        );
        remove(indexesToRemove);
      }
      isInitialized.current = true;
    }
  }, [nbDays, fields.length, append, remove]);

  return (
    <div className="flex flex-col md:flex-row gap-6 p-6 h-full">
      <div className="md:sticky md:top-20 z-10 md:w-1/3 xl:w-1/4 flex flex-col h-full">
        <RadioGroup
          defaultValue={'day' as EditorialMode}
          onValueChange={(value) => {
            setEditorialMode(value as EditorialMode);
          }}
          className="mb-2"
        >
          <div className="flex flex-row items-center gap-2">
            <RadioGroupItem value="day" id="editorial-mode-day" />
            <Label htmlFor="editorial-mode-day">
              {t('circuits.daysList.editorialMode.day')}
            </Label>
          </div>
          <div className="flex flex-row items-center gap-2">
            <RadioGroupItem value="list" id="editorial-mode-list" />
            <Label htmlFor="editorial-mode-list">
              {t('circuits.daysList.editorialMode.list')}
            </Label>
          </div>
        </RadioGroup>
        <div className="h-[calc(100vh-10rem)]">
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                itemCount={fields.length}
                itemSize={50}
                width={width}
              >
                {({ index, style }) => (
                  <DayItem
                    index={index}
                    style={style as React.CSSProperties}
                    isSelected={index === userSelectedDayIndex}
                    onClick={() => handleDayClick(index)}
                    onDuplicate={() => handleDuplicateDay(index)}
                    onRemove={() => handleRemoveDay(index)}
                    showRemove={fields.length > 1}
                    onAddDay={() => handleAddDay(index)}
                    circuitCode={circuitCode}
                    isViewMode={isViewMode}
                  />
                )}
              </List>
            )}
          </AutoSizer>
        </div>
      </div>
      {editorialMode === 'list' && (
        <div className="flex flex-col gap-4 w-full">
          {fields.map((field, index) => (
            <CircuitEditorialForm
              mode="list"
              control={control}
              index={index}
              language={language}
              days={days}
              setDisplayedDayIndex={setUserSelectedDayIndex}
              isViewMode={isViewMode}
              paragraphOverrides={paragraphOverrides}
              suppressedOverrideFields={suppressedOverrideFields}
              key={field.id}
            />
          ))}
        </div>
      )}
      {editorialMode === 'day' &&
        userSelectedDayIndex < fields.length &&
        userSelectedDayIndex >= 0 && (
          <CircuitEditorialForm
            control={control}
            language={language}
            days={days}
            mode={editorialMode}
            isViewMode={isViewMode}
            setDisplayedDayIndex={setUserSelectedDayIndex}
            index={userSelectedDayIndex}
            paragraphOverrides={paragraphOverrides}
            suppressedOverrideFields={suppressedOverrideFields}
            key={`circuit-day-${userSelectedDayIndex}`}
          />
        )}
      {modalState && (
        <CircuitDayOperationModal
          closeModal={closeModal}
          operation={modalState.operation}
          messages={modalState.messages}
          actionOnClick={modalState.action}
        />
      )}
    </div>
  );
};

export default CircuitDaysList;
